import { fg } from '@atlassian/jira-feature-gating';
import {
	createResource,
	useResource,
	getResourceStore,
	type RouteResource,
	type UseResourceHookResponse,
} from '@atlassian/react-resource-router';
import {
	getAdminSettingsByKeys,
	getBannerSettings,
	getUserSettings,
	getLogo,
	getFavicon,
} from './updateNavigationSettings.tsx';
import {
	RESOURCE_ASSETS_NAVIGATION_SETTINGS,
	RESOURCE_ASSETS_NAVIGATION_SETTINGS_KEY,
	RESOURCE_ASSETS_BANNER_SETTINGS,
	RESOURCE_ASSETS_BANNER_SETTINGS_KEY,
	RESOURCE_ASSETS_USER_SETTINGS,
	RESOURCE_ASSETS_USER_SETTINGS_KEY,
	RESOURCE_ASSETS_LOGO_FAVICON,
	RESOURCE_ASSETS_LOGO_FAVICON_KEY,
	NAVIGATION_SETTINGS_KEYS,
	DATE_TIME_SETTINGS_KEYS,
	USER_SETTING_BANNER_DISMISSAL_KEY,
	ASSETS_ADMIN_SETTINGS_MAX_AGE,
	emptyNavigationSettingsResponse,
	emptyBannerSettingsResponse,
	emptyUserSettingsResponse,
	emptyLogoResponse,
	emptyFaviconResponse,
} from './emptyLogoResponse.constants.tsx';
import { extractResourceStatePromise } from './extractResourceStatePromise.tsx';
import type {
	BannerSettings,
	UserSettingsMap,
	AssetsLogoAndFaviconData,
	AssetsNavigationSettingsResource,
} from './BannerSettings.types.tsx';

export const assetsNavigationSettingsResource: RouteResource<AssetsNavigationSettingsResource> =
	createResource<AssetsNavigationSettingsResource>({
		type: RESOURCE_ASSETS_NAVIGATION_SETTINGS,
		getKey: () => RESOURCE_ASSETS_NAVIGATION_SETTINGS_KEY,
		getData: async () => {
			if (!fg('assets-announcement-banner-settings')) {
				return emptyNavigationSettingsResponse;
			}

			const navigationSettingsRequest = [
				{ key: NAVIGATION_SETTINGS_KEYS.BG_COLOR },
				{ key: NAVIGATION_SETTINGS_KEYS.HIGHLIGHT_COLOR },
				{ key: DATE_TIME_SETTINGS_KEYS.TIMEZONE },
				{ key: NAVIGATION_SETTINGS_KEYS.SITE_TITLE },
			];
			const { storeState } = getResourceStore();
			const state = storeState.getState();

			const workspaceId = await extractResourceStatePromise(state.data);

			if (!workspaceId || typeof workspaceId !== 'string') {
				return emptyNavigationSettingsResponse;
			}

			const navigationSettings = await getAdminSettingsByKeys(
				workspaceId,
				navigationSettingsRequest,
			);
			const backgroundColorSetting = navigationSettings[NAVIGATION_SETTINGS_KEYS.BG_COLOR];

			return {
				backgroundColor: backgroundColorSetting?.value,
				highlightColor: navigationSettings[NAVIGATION_SETTINGS_KEYS.HIGHLIGHT_COLOR]?.value,
				timezone: navigationSettings[DATE_TIME_SETTINGS_KEYS.TIMEZONE]?.value,
				showSiteTitle: navigationSettings[NAVIGATION_SETTINGS_KEYS.SITE_TITLE]?.value === 'true',
				isDefaultBackground: backgroundColorSetting?.value === backgroundColorSetting?.defaultValue,
			};
		},
		maxAge: ASSETS_ADMIN_SETTINGS_MAX_AGE,
	});

export const useAssetsNavigationSettingsResource =
	(): UseResourceHookResponse<AssetsNavigationSettingsResource> => {
		return useResource<AssetsNavigationSettingsResource>(assetsNavigationSettingsResource);
	};

export const assetsBannerSettingsResource: RouteResource<BannerSettings> =
	createResource<BannerSettings>({
		type: RESOURCE_ASSETS_BANNER_SETTINGS,
		getKey: () => RESOURCE_ASSETS_BANNER_SETTINGS_KEY,
		getData: async () => {
			if (!fg('assets-announcement-banner-settings')) {
				return emptyBannerSettingsResponse;
			}

			const { storeState } = getResourceStore();
			const state = storeState.getState();

			const workspaceId = await extractResourceStatePromise(state.data);

			if (!workspaceId || typeof workspaceId !== 'string') {
				return emptyBannerSettingsResponse;
			}

			const bannerSettings = await getBannerSettings(workspaceId);

			return bannerSettings;
		},
		maxAge: ASSETS_ADMIN_SETTINGS_MAX_AGE,
	});

export const useAssetsBannerSettingsResource = (): UseResourceHookResponse<BannerSettings> => {
	return useResource<BannerSettings>(assetsBannerSettingsResource);
};

export const assetsUserSettingsResource: RouteResource<UserSettingsMap> =
	createResource<UserSettingsMap>({
		type: RESOURCE_ASSETS_USER_SETTINGS,
		getKey: () => RESOURCE_ASSETS_USER_SETTINGS_KEY,
		getData: async () => {
			if (!fg('assets-announcement-banner-settings')) {
				return emptyUserSettingsResponse;
			}

			const { storeState } = getResourceStore();
			const state = storeState.getState();

			const workspaceId = await extractResourceStatePromise(state.data);

			if (!workspaceId || typeof workspaceId !== 'string') {
				return emptyUserSettingsResponse;
			}

			const userSettings = await getUserSettings(workspaceId, USER_SETTING_BANNER_DISMISSAL_KEY);

			return {
				[USER_SETTING_BANNER_DISMISSAL_KEY]: userSettings.value,
			};
		},
		maxAge: ASSETS_ADMIN_SETTINGS_MAX_AGE,
	});

export const useAssetsUserSettingsResource = (): UseResourceHookResponse<UserSettingsMap> => {
	return useResource<UserSettingsMap>(assetsUserSettingsResource);
};

export const assetsLogoAndFaviconResource: RouteResource<AssetsLogoAndFaviconData> =
	createResource<AssetsLogoAndFaviconData>({
		type: RESOURCE_ASSETS_LOGO_FAVICON,
		getKey: () => RESOURCE_ASSETS_LOGO_FAVICON_KEY,
		getData: async () => {
			if (!fg('assets-announcement-banner-settings')) {
				return { logo: emptyLogoResponse, favicon: emptyFaviconResponse };
			}

			const { storeState } = getResourceStore();
			const state = storeState.getState();

			const workspaceId = await extractResourceStatePromise(state.data);

			if (!workspaceId || typeof workspaceId !== 'string') {
				return { logo: emptyLogoResponse, favicon: emptyFaviconResponse };
			}

			const [logoResponse, faviconResponse] = await Promise.all([
				getLogo(workspaceId).catch(() => emptyLogoResponse),
				getFavicon(workspaceId).catch(() => emptyFaviconResponse),
			]);

			return {
				logo: logoResponse,
				favicon: faviconResponse,
			};
		},
		maxAge: ASSETS_ADMIN_SETTINGS_MAX_AGE,
	});

export const useAssetsLogoAndFaviconResource =
	(): UseResourceHookResponse<AssetsLogoAndFaviconData> => {
		return useResource<AssetsLogoAndFaviconData>(assetsLogoAndFaviconResource);
	};
